import { CommonModule, Location } from '@angular/common';
import { Component, OnInit, Optional } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MessageService, SelectItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { PageMetric } from '../../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../../shared/page-layout/page-metric-strip.component';
import { PageDetailHeroComponent } from '../../../shared/page-layout/page-detail-hero.component';
import { PageFormSectionComponent } from '../../../shared/page-layout/page-form-section.component';
import { SessionStorageService } from '../../../services/session-storage.service';
import { ProductVariant } from '../../models/product-variant.model';
import { ProductType } from '../../models/product-type.model';
import { ProductCrmClientItemCode, ProductLogisticsUsage, ProductLogisticsUsageModule, Products } from '../../models/product.model';
import { ProductsService } from '../../product.service';
import { CrmClient } from '../../../crm/crm.model';
import { CrmService } from '../../../crm/crm.service';
import { WarehousingMasterData } from '../../../warehousing/warehousing.model';
import { WarehousingService } from '../../../warehousing/warehousing.service';
import { APIResponseWrapper } from '../../../models/common/api-response-wrapper';
import { catchError, forkJoin, of } from 'rxjs';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ChartOfAccounts } from '../../../chart-of-accounts/chart-of-accounts.model';
import { ChartOfAccountsService } from '../../../chart-of-accounts/chart-of-accounts.service';
import { ShipmentChargeType } from '../../../shipment/models/charge-line/shipment-charge-type.enum';

@Component({
    selector: 'app-add-edit-product',
    templateUrl: './add-edit-product.component.html',
    styleUrls: ['./add-edit-product.component.css'],
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        RouterModule,
        ToastModule,
        InputTextModule,
        ButtonModule,
        InputNumberModule,
        TextareaModule,
        SelectModule,
        ProgressBarModule,
        InputGroupModule,
        InputGroupAddonModule,
        IftaLabelModule,
        PageMetricStripComponent,
        PageDetailHeroComponent,
        PageFormSectionComponent
    ],
    providers: [
        MessageService,
        ProductsService,
        SessionStorageService
    ]
})
export class AddEditProductComponent implements OnInit {
    product!: Products;
    productForm!: FormGroup;
    editMode = false;
    isDialogMode = false;
    productId!: number;
    isLoading = false;
    submitted = false;
    productTypes: SelectItem<string>[] = [];
    shipmentChargeTypes: SelectItem<ShipmentChargeType>[] = [];
    logisticsUsageModules: SelectItem<ProductLogisticsUsageModule>[] = [
        { label: 'Shipment Charge', value: 'SHIPMENT_CHARGE' },
        { label: 'Customs Clearance', value: 'CUSTOMS_CLEARANCE' },
        { label: 'Container Yard', value: 'CONTAINER_YARD' },
        { label: 'Warehouse Billing', value: 'WAREHOUSE_BILLING' },
        { label: 'POSM Execution', value: 'POSM_EXECUTION' },
        { label: 'Logistics Project', value: 'LOGISTICS_PROJECT' }
    ];
    crmClients: SelectItem<number>[] = [];
    inventoryUoms: SelectItem<number>[] = [];
    warehouses: SelectItem<number>[] = [];
    itemConditions: SelectItem<number>[] = [];
    glAccounts: SelectItem<number>[] = [];
    readonly inventoryTrackingModes: SelectItem<string>[] = [
        { label: 'Lot tracked', value: 'LOT' },
        { label: 'Serial tracked', value: 'SERIAL' },
        { label: 'Quantity only', value: 'NONE' }
    ];

    constructor(
        private fb: FormBuilder,
        private route: ActivatedRoute,
        private location: Location,
        private messageService: MessageService,
        private productService: ProductsService,
        private crmService: CrmService,
        private warehousingService: WarehousingService,
        private chartOfAccountsService: ChartOfAccountsService,
        private sessionStorageService: SessionStorageService,
        private router: Router,
        @Optional() private dialogRef: DynamicDialogRef | null,
        @Optional() private dialogConfig: DynamicDialogConfig | null
    ) {}

    ngOnInit(): void {
        this.product = new Products();
        this.loadProductTypes();
        this.initForm();
        this.loadCrmClients();
        this.loadInventoryReferenceData();
        this.loadGlAccounts();
        this.configureInventoryValidation();
        this.productForm.get('inventoryManaged')?.valueChanges.subscribe(() => this.configureInventoryValidation());
        this.configureShipmentChargeValidation();
        this.productForm.get('productType')?.valueChanges.subscribe(() => this.configureShipmentChargeValidation());
        this.productForm.get('shipmentChargeEnabled')?.valueChanges.subscribe(() => this.configureShipmentChargeValidation());
        this.isDialogMode = !!this.dialogRef;

        this.route.params.subscribe(params => {
            this.productId = this.isDialogMode
                ? Number(this.dialogConfig?.data?.productId)
                : Number(params['id']);
            this.editMode = !!this.productId;
            if (this.editMode) {
                this.loadProduct();
                return;
            }
            if (this.dialogConfig?.data?.presetShipmentCharge) {
                this.productForm.patchValue({
                    productType: ProductType.SERVICE,
                    inventoryManaged: false,
                    shipmentChargeEnabled: true,
                    defaultShipmentChargeType: ShipmentChargeType.TRANSPORT
                });
                this.logisticsUsages.push(this.createLogisticsUsageFormGroup({
                    usageModule: 'SHIPMENT_CHARGE',
                    operationalCode: ShipmentChargeType.TRANSPORT,
                    active: true
                }));
                this.configureShipmentChargeValidation();
            }
            this.ensureVariantRow();
        });
    }

    get productVariants(): FormArray {
        return this.productForm.get('productVariants') as FormArray;
    }

    get crmClientItemCodes(): FormArray {
        return this.productForm.get('crmClientItemCodes') as FormArray;
    }

    get logisticsUsages(): FormArray {
        return this.productForm.get('logisticsUsages') as FormArray;
    }

    metrics(): PageMetric[] {
        return [
            {
                label: 'Mode',
                value: this.editMode ? 'Edit' : 'Create',
                hint: this.editMode ? `Product #${this.productId}` : 'New catalog setup',
                tone: this.editMode ? 'info' : 'success'
            },
            {
                label: 'Variants',
                value: this.productVariants.length,
                hint: 'Canonical priced rows',
                tone: 'warn'
            },
            {
                label: 'Lead Sell',
                value: this.formatAmount(this.primaryVariantSellingRate()),
                hint: 'First variant selling price'
            },
            {
                label: 'Tax Rate',
                value: `${this.formatAmount(this.productForm?.get('taxRate')?.value)}%`,
                hint: `${this.productForm?.get('currencyCode')?.value || 'SAR'} shared default`
            }
        ];
    }

    addProductVariant(): void {
        this.productVariants.push(this.createProductVariantFormGroup());
    }

    removeProductVariant(index: number): void {
        if (this.productVariants.at(index)?.get('systemStandard')?.value) {
            return;
        }
        if (this.productVariants.length === 1) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Variants',
                detail: 'At least one variant row should remain in the workspace.'
            });
            return;
        }
        this.productVariants.removeAt(index);
    }

    addCrmClientItemCode(): void {
        this.crmClientItemCodes.push(this.createCrmClientItemCodeFormGroup());
    }

    removeCrmClientItemCode(index: number): void {
        this.crmClientItemCodes.removeAt(index);
    }

    addLogisticsUsage(): void {
        this.logisticsUsages.push(this.createLogisticsUsageFormGroup());
    }

    removeLogisticsUsage(index: number): void {
        if (this.logisticsUsages.at(index)?.get('systemStandard')?.value) return;
        this.logisticsUsages.removeAt(index);
    }

    operationalCodeOptions(module: ProductLogisticsUsageModule | null | undefined): SelectItem<string>[] {
        const codes: Record<ProductLogisticsUsageModule, string[]> = {
            SHIPMENT_CHARGE: Object.values(ShipmentChargeType),
            CUSTOMS_CLEARANCE: ['CUSTOMS_CLEARANCE', 'LOCAL_TRANSPORT', 'PORT_DUES', 'DELIVERY_ORDER',
                'LABOR', 'HANDLING', 'DOCUMENTATION', 'CUSTOMS_INSPECTION'],
            CONTAINER_YARD: ['STORAGE', 'DEMURRAGE', 'DETENTION', 'LIFT_ON', 'LIFT_OFF', 'GATE_IN',
                'GATE_OUT', 'REEFER_PLUGIN', 'INSPECTION', 'WEIGHING', 'CLEANING', 'REPAIR'],
            WAREHOUSE_BILLING: ['STORAGE', 'HANDLING_IN', 'HANDLING_OUT', 'PICKING', 'PACKING',
                'LABELING', 'PALLETIZATION', 'DEPALLETIZATION', 'QUALITY_CHECK', 'LOADING', 'UNLOADING',
                'CROSS_DOCK', 'INVENTORY_COUNT', 'REWORK', 'SCRAPPING', 'REFURBISHMENT',
                'DESTRUCTION', 'OTHER'],
            POSM_EXECUTION: ['DELIVERY_SCHEDULE'],
            LOGISTICS_PROJECT: ['TRANSPORT', 'CUSTOMS_CLEARANCE', 'WAREHOUSE_STORAGE', 'CONTAINER_YARD',
                'POSM_EXECUTION', 'LABOR', 'HANDLING', 'DOCUMENTATION', 'OTHER']
        };
        return (module ? codes[module] : []).map(value => ({
            value,
            label: value.split('_').map(part => part.charAt(0) + part.slice(1).toLowerCase()).join(' ')
        }));
    }

    saveProduct(): void {
        this.submitted = true;
        if (this.productForm.invalid) {
            this.productForm.markAllAsTouched();
            this.messageService.add({
                severity: 'error',
                summary: 'Validation Error',
                detail: 'Please correct all product form errors before saving.'
            });
            return;
        }

        this.isLoading = true;
        const modifiedProduct = this.productForm.getRawValue() as Products;
        const userId = this.sessionStorageService.getCustomer()!.id;

        if (!modifiedProduct.productVariants?.length) {
            this.isLoading = false;
            this.messageService.add({
                severity: 'error',
                summary: 'Validation Error',
                detail: 'At least one priced variant is required before saving.'
            });
            return;
        }

        modifiedProduct.owningCompanyId = this.sessionStorageService.getCompany()!.id;
        modifiedProduct.updatedAt = new Date();
        modifiedProduct.updatedBy = userId;
        modifiedProduct.taxRate = modifiedProduct.taxRate ?? 0;
        modifiedProduct.currencyCode = this.normalizeCurrencyCode(
            modifiedProduct.currencyCode || this.defaultCurrencyCode()
        );
        this.applyDerivedProductCommercials(modifiedProduct);
        modifiedProduct.inventoryProfiles = modifiedProduct.inventoryManaged
            ? modifiedProduct.productVariants.map((variant, index) => {
                const existing = this.product.inventoryProfiles?.find(profile => profile.variantId === variant.variantID)
                    || this.product.inventoryProfiles?.[index];
                const baseUomId = Number(this.productForm.get('baseUomId')?.value) || undefined;
                const purchaseUomId = Number(this.productForm.get('defaultPurchaseUomId')?.value) || baseUomId;
                const salesUomId = Number(this.productForm.get('defaultSalesUomId')?.value) || baseUomId;
                return {
                    ...existing,
                    variantId: variant.variantID,
                    itemNumber: variant.variantCode,
                    skuNumber: variant.variantCode,
                    stockOwnerType: existing?.stockOwnerType || 'TENANT_OWNED',
                    baseUomId,
                    defaultPurchaseUomId: purchaseUomId,
                    defaultSalesUomId: salesUomId,
                    defaultWarehouseId: Number(this.productForm.get('defaultWarehouseId')?.value) || undefined,
                    defaultConditionId: Number(this.productForm.get('defaultConditionId')?.value) || undefined,
                    trackingMode: this.productForm.get('trackingMode')?.value || 'LOT',
                    shelfLifeDays: Number(this.productForm.get('shelfLifeDays')?.value) || undefined,
                    minimumTemperatureC: this.productForm.get('minimumTemperatureC')?.value,
                    maximumTemperatureC: this.productForm.get('maximumTemperatureC')?.value,
                    uomConversions: [
                        ...(purchaseUomId && purchaseUomId !== baseUomId ? [{
                            uomId: purchaseUomId, usageType: 'PURCHASE' as const,
                            factorToBase: Number(this.productForm.get('purchaseFactor')?.value) || 1, active: true
                        }] : []),
                        ...(salesUomId && salesUomId !== baseUomId ? [{
                            uomId: salesUomId, usageType: 'SALES' as const,
                            factorToBase: Number(this.productForm.get('salesFactor')?.value) || 1, active: true
                        }] : [])
                    ]
                };
            }) : [];
        this.removeInventoryFormFields(modifiedProduct);

        if (this.editMode) {
            modifiedProduct.createdAt = this.product.createdAt;
            modifiedProduct.createdBy = this.product.createdBy;
        } else {
            modifiedProduct.createdAt = new Date();
            modifiedProduct.createdBy = userId;
        }

        modifiedProduct.productVariants.forEach(variant => {
            variant.owningCompanyId = this.sessionStorageService.getCompany()!.id;
            variant.updatedAt = new Date();
            variant.updatedBy = userId;

            if (variant.variantID) {
                const originalVariant = this.product.productVariants?.find(item => item.variantID === variant.variantID);
                if (originalVariant) {
                    variant.createdAt = originalVariant.createdAt;
                    variant.createdBy = originalVariant.createdBy;
                }
            } else {
                variant.createdAt = new Date();
                variant.createdBy = userId;
            }
        });

        const save$ = this.editMode
            ? this.productService.updateProduct(this.productId, modifiedProduct)
            : this.productService.createProduct(modifiedProduct);
        save$.subscribe({
            next: (savedProduct) => {
                this.isLoading = false;
                this.messageService.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Product saved successfully.'
                });
                if (this.isDialogMode && this.dialogRef) {
                    this.dialogRef.close(savedProduct);
                    return;
                }
                this.router.navigate(['/order/products']);
            },
            error: (error) => {
                this.isLoading = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: this.extractErrorDetail(error, 'Error saving product.')
                });
            }
        });
    }

    cancel(): void {
        if (this.isDialogMode && this.dialogRef) {
            this.dialogRef.close();
            return;
        }
        this.location.back();
    }

    hasError(controlName: string): boolean {
        const control = this.productForm.get(controlName);
        return !!control && control.invalid && (control.touched || this.submitted);
    }

    hasVariantError(index: number, controlName: string): boolean {
        const control = this.productVariants.at(index)?.get(controlName);
        return !!control && control.invalid && (control.touched || this.submitted);
    }

    private loadProduct(): void {
        this.isLoading = true;
        this.productService.getProductById(this.productId).subscribe({
            next: (product: Products) => {
                this.product = product;
                const { productType, ...productWithoutProductType } = product;
                productWithoutProductType.currencyCode = this.normalizeCurrencyCode(product.currencyCode || this.defaultCurrencyCode());
                this.productForm.patchValue(productWithoutProductType);
                const profile = product.inventoryProfiles?.[0];
                if (profile) {
                    const purchase = profile.uomConversions?.find(row => row.usageType === 'PURCHASE' || row.usageType === 'BOTH');
                    const sales = profile.uomConversions?.find(row => row.usageType === 'SALES' || row.usageType === 'BOTH');
                    this.productForm.patchValue({
                        inventoryManaged: product.inventoryManaged,
                        baseUomId: profile.baseUomId,
                        defaultPurchaseUomId: profile.defaultPurchaseUomId,
                        defaultSalesUomId: profile.defaultSalesUomId,
                        defaultWarehouseId: profile.defaultWarehouseId,
                        defaultConditionId: profile.defaultConditionId,
                        trackingMode: profile.trackingMode || 'LOT',
                        shelfLifeDays: profile.shelfLifeDays,
                        minimumTemperatureC: profile.minimumTemperatureC,
                        maximumTemperatureC: profile.maximumTemperatureC,
                        purchaseFactor: purchase?.factorToBase || 1,
                        salesFactor: sales?.factorToBase || 1
                    });
                }
                this.productForm.get('productType')?.setValue(productType?.valueOf());
                this.productForm.setControl(
                    'productVariants',
                    this.fb.array((product.productVariants || []).map(variant => this.createProductVariantFormGroup(variant, product)))
                );
                this.productForm.setControl(
                    'crmClientItemCodes',
                    this.fb.array((product.crmClientItemCodes || []).map(mapping => this.createCrmClientItemCodeFormGroup(mapping)))
                );
                this.productForm.setControl(
                    'logisticsUsages',
                    this.fb.array((product.logisticsUsages || []).map(usage => this.createLogisticsUsageFormGroup(usage)))
                );
                if (product.systemStandard) {
                    [
                        'productCode',
                        'productType',
                        'inventoryManaged',
                        'shipmentChargeEnabled',
                        'defaultShipmentChargeType'
                    ].forEach(controlName => this.productForm.get(controlName)?.disable({ emitEvent: false }));
                    this.logisticsUsages.controls.forEach(control => {
                        if (control.get('systemStandard')?.value) control.disable({ emitEvent: false });
                    });
                    this.productVariants.controls.forEach(control => {
                        if (control.get('systemStandard')?.value) {
                            control.get('name')?.disable({ emitEvent: false });
                            control.get('variantCode')?.disable({ emitEvent: false });
                        }
                    });
                }
                this.ensureVariantRow(product);
                this.isLoading = false;
            },
            error: () => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Error loading product.'
                });
                this.isLoading = false;
            }
        });
    }

    private loadProductTypes(): void {
        this.productTypes = Object.values(ProductType).map(type => ({
            value: type,
            label: type
        }));
        this.shipmentChargeTypes = Object.values(ShipmentChargeType).map(type => ({
            value: type,
            label: type.split('_').map(part => part.charAt(0) + part.slice(1).toLowerCase()).join(' ')
        }));
    }

    private initForm(): void {
        this.productForm = this.fb.group({
            productID: [null],
            name: ['', Validators.required],
            productCode: ['', Validators.required],
            productType: [null, Validators.required],
            taxRate: [0],
            currencyCode: [this.defaultCurrencyCode(), Validators.required],
            incomeAccountId: [null],
            expenseAccountId: [null],
            shipmentChargeEnabled: [false],
            defaultShipmentChargeType: [null],
            description: [''],
            descriptionLocal: [''],
            inventoryManaged: [false],
            baseUomId: [null],
            defaultPurchaseUomId: [null],
            defaultSalesUomId: [null],
            defaultWarehouseId: [null],
            defaultConditionId: [null],
            trackingMode: ['LOT'],
            shelfLifeDays: [null],
            minimumTemperatureC: [-25],
            maximumTemperatureC: [-18],
            purchaseFactor: [1],
            salesFactor: [1],
            createdAt: [null],
            createdBy: [null],
            updatedAt: [null],
            updatedBy: [null],
            productVariants: this.fb.array([]),
            crmClientItemCodes: this.fb.array([])
            ,
            logisticsUsages: this.fb.array([])
        });
    }

    private createProductVariantFormGroup(productVariant?: ProductVariant, product?: Products): FormGroup {
        return this.fb.group({
            variantID: [productVariant?.variantID ?? null],
            name: [productVariant?.name ?? '', Validators.required],
            variantCode: [productVariant?.variantCode ?? '', Validators.required],
            rateAdjustment: [this.resolveVariantStandardRate(productVariant, product)],
            buyingRateAdjustment: [this.resolveVariantBuyingRate(productVariant, product)],
            sellingRateAdjustment: [this.resolveVariantSellingRate(productVariant, product)],
            systemStandard: [productVariant?.systemStandard === true],
            createdAt: [productVariant?.createdAt ?? null],
            createdBy: [productVariant?.createdBy ?? null],
            updatedAt: [productVariant?.updatedAt ?? null],
            updatedBy: [productVariant?.updatedBy ?? null]
        });
    }

    private createCrmClientItemCodeFormGroup(mapping?: ProductCrmClientItemCode): FormGroup {
        return this.fb.group({
            id: [mapping?.id ?? null],
            crmClientId: [mapping?.crmClientId ?? null, Validators.required],
            clientItemCode: [mapping?.clientItemCode ?? '', Validators.required],
            clientItemDescription: [mapping?.clientItemDescription ?? ''],
            active: [mapping?.active !== false]
        });
    }

    private createLogisticsUsageFormGroup(usage?: ProductLogisticsUsage): FormGroup {
        return this.fb.group({
            id: [usage?.id ?? null],
            productId: [usage?.productId ?? null],
            usageModule: [usage?.usageModule ?? 'SHIPMENT_CHARGE', Validators.required],
            operationalCode: [usage?.operationalCode ?? null, Validators.required],
            defaultVariantId: [usage?.defaultVariantId ?? null],
            active: [usage?.active !== false],
            systemStandard: [usage?.systemStandard === true]
        });
    }

    private loadCrmClients(): void {
        const companyId = this.sessionStorageService.getCompany()?.id;
        if (!companyId) return;
        this.crmService.getClients(companyId).subscribe({
            next: (clients: CrmClient[]) => {
                this.crmClients = (clients || [])
                    .filter(client => !!client.id)
                    .map(client => ({ label: client.companyName || client.clientNumber || `Client ${client.id}`, value: Number(client.id) }));
            }
        });
    }

    private loadInventoryReferenceData(): void {
        forkJoin({
            uoms: this.warehousingService.getMasterData('TENANT_UOM').pipe(catchError(() => of(this.emptyMasterDataResponse()))),
            warehouses: this.warehousingService.getMasterData('WAREHOUSE').pipe(catchError(() => of(this.emptyMasterDataResponse()))),
            conditions: this.warehousingService.getMasterData('ITEM_CONDITION').pipe(catchError(() => of(this.emptyMasterDataResponse())))
        }).subscribe(({ uoms, warehouses, conditions }) => {
            this.inventoryUoms = this.toMasterDataOptions(uoms.data);
            this.warehouses = this.toMasterDataOptions(warehouses.data);
            this.itemConditions = this.toMasterDataOptions(conditions.data);
        });
    }

    private loadGlAccounts(): void {
        const companyId = this.sessionStorageService.getCompany()?.id;
        if (!companyId) return;
        this.chartOfAccountsService.getAllChartOfAccountsByCompany(companyId, false).pipe(
            catchError(() => of([] as ChartOfAccounts[]))
        ).subscribe(accounts => {
            this.glAccounts = (accounts || [])
                .filter(account => account.accountId && account.isActive !== false && account.deprecated !== true)
                .map(account => ({
                    value: account.accountId,
                    label: account.accountCode ? `${account.accountCode} - ${account.accountName}` : account.accountName
                }));
        });
    }

    private configureInventoryValidation(): void {
        const inventoryManaged = !!this.productForm.get('inventoryManaged')?.value;
        if (inventoryManaged && this.productForm.get('shipmentChargeEnabled')?.value) {
            this.productForm.patchValue({
                shipmentChargeEnabled: false,
                defaultShipmentChargeType: null
            }, { emitEvent: false });
        }
        for (const controlName of ['baseUomId', 'defaultWarehouseId']) {
            const control = this.productForm.get(controlName);
            control?.setValidators(inventoryManaged ? [Validators.required] : []);
            control?.updateValueAndValidity({ emitEvent: false });
        }
    }

    private configureShipmentChargeValidation(): void {
        const serviceProduct = this.productForm.get('productType')?.value === ProductType.SERVICE;
        const enabledControl = this.productForm.get('shipmentChargeEnabled');
        const typeControl = this.productForm.get('defaultShipmentChargeType');

        if (!serviceProduct) {
            enabledControl?.setValue(false, { emitEvent: false });
            typeControl?.setValue(null, { emitEvent: false });
            for (let index = this.logisticsUsages.length - 1; index >= 0; index--) {
                if (!this.logisticsUsages.at(index).get('systemStandard')?.value) {
                    this.logisticsUsages.removeAt(index);
                }
            }
        }
        if (serviceProduct && enabledControl?.value) {
            this.productForm.get('inventoryManaged')?.setValue(false, { emitEvent: false });
            typeControl?.setValidators([Validators.required]);
        } else {
            typeControl?.clearValidators();
        }
        typeControl?.updateValueAndValidity({ emitEvent: false });
        this.configureInventoryValidation();
    }

    private emptyMasterDataResponse(): APIResponseWrapper<WarehousingMasterData[]> {
        return { success: false, message: '', responseCode: '', data: [] };
    }

    private toMasterDataOptions(items: WarehousingMasterData[] | undefined): SelectItem<number>[] {
        return (items || [])
            .filter(item => item.id && item.isActive !== false)
            .map(item => ({
                value: item.id!,
                label: item.code ? `${item.name} (${item.code})` : item.name || 'Unnamed option'
            }));
    }

    private ensureVariantRow(product?: Products): void {
        if (!this.productVariants.length) {
            this.productVariants.push(this.createProductVariantFormGroup(undefined, product));
        }
    }

    private primaryVariantSellingRate(): number {
        const variants = (this.productVariants.getRawValue() as ProductVariant[]) || [];
        const primaryVariant = variants[0];
        return this.resolveVariantSellingRate(primaryVariant);
    }

    private applyDerivedProductCommercials(product: Products): void {
        const primaryVariant = product.productVariants?.[0];
        product.standardRate = this.resolveVariantStandardRate(primaryVariant);
        product.buyingRate = this.resolveVariantBuyingRate(primaryVariant);
        product.sellingRate = this.resolveVariantSellingRate(primaryVariant);
    }

    /** Inventory controls are form helpers; the API accepts them only within inventoryProfiles. */
    private removeInventoryFormFields(product: Products): void {
        const payload = product as Products & Record<string, unknown>;
        [
            'baseUomId',
            'defaultPurchaseUomId',
            'defaultSalesUomId',
            'defaultWarehouseId',
            'defaultConditionId',
            'trackingMode',
            'shelfLifeDays',
            'minimumTemperatureC',
            'maximumTemperatureC',
            'purchaseFactor',
            'salesFactor'
        ].forEach(field => delete payload[field]);
    }

    private resolveVariantStandardRate(variant?: ProductVariant | null, product?: Products): number {
        return this.resolveAmount(variant?.rateAdjustment, product?.standardRate, 0);
    }

    private resolveVariantBuyingRate(variant?: ProductVariant | null, product?: Products): number {
        return this.resolveAmount(variant?.buyingRateAdjustment, variant?.rateAdjustment, product?.buyingRate, product?.standardRate, 0);
    }

    private resolveVariantSellingRate(variant?: ProductVariant | null, product?: Products): number {
        return this.resolveAmount(variant?.sellingRateAdjustment, variant?.rateAdjustment, product?.sellingRate, product?.standardRate, 0);
    }

    private resolveAmount(...candidates: Array<number | null | undefined>): number {
        for (const candidate of candidates) {
            if (candidate !== null && candidate !== undefined && !Number.isNaN(Number(candidate))) {
                return Number(candidate);
            }
        }
        return 0;
    }

    private defaultCurrencyCode(): string {
        const company = this.sessionStorageService.getCompany();
        const currency = this.sessionStorageService.getCountryList()
            ?.find(country => country.id === company?.countryId)?.currencyCode;
        return this.normalizeCurrencyCode(currency);
    }

    private formatAmount(value?: number | null): string {
        return Number(value || 0).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    }

    private normalizeCurrencyCode(value: string | null | undefined): string {
        const normalized = (value ?? '').toString().trim().toUpperCase();
        if (!normalized || normalized === 'NULL' || normalized === 'UNDEFINED') {
            return 'SAR';
        }
        return normalized;
    }

    private extractErrorDetail(error: any, fallback: string): string {
        return error?.error?.message || error?.error?.error || error?.message || fallback;
    }
}
